/**
 * A customer's ledger from TallyPrime, read on request through the office's paired connector
 * (docs/product/statement/01-LEDGER-PLAN.md; owner's decisions §15, 17 Oct 2026; built 24 Oct 2026).
 *
 * The rules every side shares, with no I/O:
 *
 *   the period     the current financial year by default, at most 24 months, never ending after today;
 *   the answer     what a connector may send back, checked whole by the server before anything is kept:
 *                  the request's own bindings repeated, whole paise, dates inside the period and in order,
 *                  and opening + every line = closing, to the paisa;
 *   the display    the running balance, the totals, and the spreadsheet.
 *
 * Amounts are whole paise in the product's sign: positive is owed to the company (Dr), negative is held for
 * the customer (Cr). A line is one voucher, its customer lines netted, as TallyPrime's own ledger screen shows
 * it. No narration, no reference, no other side of the entry: nothing about how the customer paid (§3, 5, 14).
 */
import { csvFileName, dateCell, numberCell, textCell, toCsv, type CsvColumn } from './csv.js';
import { addDays } from './receivablesFacts.js';

/** The longest period one request may cover (owner, §15.4). */
export const LEDGER_MAX_MONTHS = 24;
/** The most lines one answer may hold; a longer ledger is asked for a shorter period (§3.7). */
export const LEDGER_MAX_LINES = 5_000;
/** The most bill references one line may name. */
export const LEDGER_MAX_BILLS_PER_LINE = 50;
/** How long an answer is kept, then cleared: never kept as history (owner, §15.6). */
export const LEDGER_ANSWER_MINUTES = 15;
/** The largest answer the server reads. */
export const LEDGER_MAX_ANSWER_BYTES = 1024 * 1024 + 64 * 1024;
/** The only version of the request and answer this side speaks; anything else is refused, never guessed at. */
export const LEDGER_PROTOCOL_VERSION = 1;

/** How a voucher's line met a bill, as TallyPrime names it. */
export type LedgerBillKind = 'new_ref' | 'agst_ref' | 'advance' | 'on_account' | 'other';
export const LEDGER_BILL_KINDS: readonly LedgerBillKind[] = ['new_ref', 'agst_ref', 'advance', 'on_account', 'other'];

export interface LedgerBill {
    /** The bill's reference ("TA-001"); empty for "on account". */
    ref: string;
    kind: LedgerBillKind;
    /** In the product's sign: positive raises what is owed. */
    amountPaise: number;
}

export interface LedgerLine {
    /** YYYY-MM-DD */
    date: string;
    /** The company's own voucher type name ("Sales", "Bank Receipt"). */
    type: string;
    number: string | null;
    debitPaise: number;
    creditPaise: number;
    bills: LedgerBill[];
}

/** Why a connector could not read the ledger, in the protocol's words (§5.2). */
export type LedgerFailure =
    | 'tally_unavailable' | 'company_not_loaded' | 'company_changed' | 'not_a_debtor' | 'too_long' | 'did_not_add_up'
    | 'timeout' | 'refused';
export const LEDGER_FAILURES: readonly LedgerFailure[] = [
    'tally_unavailable', 'company_not_loaded', 'company_changed', 'not_a_debtor', 'too_long', 'did_not_add_up', 'timeout', 'refused',
];

/** What a request binds: the server names these, the connector repeats them, the server checks they are the same. */
export interface LedgerBinding {
    request: string;
    /** TallyPrime's company ID. */
    company: string;
    /** TallyPrime's ledger ID (GUID), lower case. */
    ledger: string;
    from: string;
    to: string;
}

export interface LedgerRead extends LedgerBinding {
    v: typeof LEDGER_PROTOCOL_VERSION;
    outcome: 'read';
    /** The balance at the start of `from`. */
    openingPaise: number;
    /** The balance at the end of `to`: opening and every line. */
    closingPaise: number;
    /** TallyPrime's closing balance as it read it now, for comparing with the last sync's. */
    tallyClosingPaise: number;
    lines: LedgerLine[];
    readAt: string;
}

export interface LedgerFailed extends LedgerBinding {
    v: typeof LEDGER_PROTOCOL_VERSION;
    outcome: LedgerFailure;
}

export type LedgerAnswer = LedgerRead | LedgerFailed;

/* ---------------------------------------------------------------------------------------------------- */
/* The period                                                                                            */
/* ---------------------------------------------------------------------------------------------------- */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whether `iso` is a real calendar day written YYYY-MM-DD. */
export function isIsoDate(iso: unknown): iso is string {
    if (typeof iso !== 'string') return false;
    const m = ISO_DATE.exec(iso);
    if (!m) return false;
    const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!));
    return d.getUTCFullYear() === +m[1]! && d.getUTCMonth() === +m[2]! - 1 && d.getUTCDate() === +m[3]!;
}

const toDate = (iso: string) => new Date(`${iso}T00:00:00Z`);
const toIso = (d: Date) => d.toISOString().slice(0, 10);

/** The same day `months` later, held to the month's last day (31 Jan + 1 month = 28 or 29 Feb). */
export function addMonths(iso: string, months: number): string {
    const d = toDate(iso);
    const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1));
    const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    return toIso(new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d.getUTCDate(), last))));
}

/** The Indian financial year a day falls in: 1 April to 31 March. */
export function financialYearOf(iso: string): { from: string; to: string } {
    const d = toDate(iso);
    const start = d.getUTCMonth() >= 3 ? d.getUTCFullYear() : d.getUTCFullYear() - 1;
    return { from: `${start}-04-01`, to: `${start + 1}-03-31` };
}

/** The last day a period starting on `from` may end on: 24 months, less a day. */
export const ledgerLatestEnd = (from: string): string => addDays(addMonths(from, LEDGER_MAX_MONTHS), -1);

export type LedgerPeriodKey = 'this_fy' | 'last_fy' | 'last_3_months';
export interface LedgerPeriod { key?: LedgerPeriodKey; from: string; to: string }

/**
 * The periods offered in one press: this financial year to today (the default, owner §15.4), the whole of
 * last financial year, and the last three months. Each is held to the books' start when that is known.
 */
export function ledgerPresets(today: string, booksFrom?: string | null): LedgerPeriod[] {
    const fy = financialYearOf(today);
    const last = financialYearOf(addDays(fy.from, -1));
    const floor = (from: string) => (booksFrom && isIsoDate(booksFrom) && from < booksFrom ? booksFrom : from);
    const presets: LedgerPeriod[] = [
        { key: 'this_fy', from: floor(fy.from), to: today },
        { key: 'last_fy', from: floor(last.from), to: last.to },
        { key: 'last_3_months', from: floor(addDays(addMonths(today, -3), 1)), to: today },
    ];
    // A period wholly before the books begin has nothing to show: not offered.
    return presets.filter(p => p.from <= p.to);
}

export const defaultLedgerPeriod = (today: string, booksFrom?: string | null): LedgerPeriod =>
    ledgerPresets(today, booksFrom)[0] ?? { key: 'this_fy', from: today, to: today };

export type LedgerPeriodProblem = 'not_a_date' | 'ends_before_it_starts' | 'ends_after_today' | 'longer_than_24_months' | 'before_the_books';

/** Why a period cannot be asked for, or null when it can. The database checks the same rules again. */
export function ledgerPeriodProblem(from: unknown, to: unknown, today: string, booksFrom?: string | null): LedgerPeriodProblem | null {
    if (!isIsoDate(from) || !isIsoDate(to)) return 'not_a_date';
    if (to < from) return 'ends_before_it_starts';
    if (to > today) return 'ends_after_today';
    if (to > ledgerLatestEnd(from)) return 'longer_than_24_months';
    if (booksFrom && isIsoDate(booksFrom) && from < booksFrom) return 'before_the_books';
    return null;
}

export const LEDGER_PERIOD_WORDS: Record<LedgerPeriodProblem, string> = {
    not_a_date: 'Choose a start and an end date.',
    ends_before_it_starts: 'The end date is before the start date.',
    ends_after_today: 'The period cannot end after today.',
    longer_than_24_months: 'Choose a period of at most 24 months.',
    before_the_books: 'TallyPrime’s books for this company start later than that. Choose a later start date.',
};

/* ---------------------------------------------------------------------------------------------------- */
/* The answer, as the server checks it                                                                   */
/* ---------------------------------------------------------------------------------------------------- */

/** The largest amount a line or balance may state: a thousand crore, in paise. Anything larger is a mistake. */
const MAX_PAISE = 1_000_000_000_000_000 / 100;

const isPaise = (n: unknown): n is number => typeof n === 'number' && Number.isSafeInteger(n) && Math.abs(n) <= MAX_PAISE;
const isCount = (n: unknown): n is number => isPaise(n) && n >= 0;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;
const text = (v: unknown, max: number, allowEmpty = false): v is string =>
    typeof v === 'string' && v.length <= max && !CONTROL.test(v) && (allowEmpty || v.trim() !== '');
const GUID = /^[a-z0-9][a-z0-9-]{7,79}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export type LedgerAnswerCheck =
    | { ok: true; answer: LedgerAnswer }
    | { ok: false; problem: string; /** The figures did not add up: the request fails as the connector's own refusal would. */ didNotAddUp?: boolean };

/**
 * A connector's answer, checked whole against the request it took (§5.2). Refused whole on any problem;
 * nothing of a refused answer is kept. The figures are recomputed here, not taken on the connector's word.
 */
export function checkLedgerAnswer(body: unknown, expected: LedgerBinding): LedgerAnswerCheck {
    const b = (typeof body === 'object' && body !== null && !Array.isArray(body) ? body : {}) as Record<string, unknown>;
    if (b['v'] !== LEDGER_PROTOCOL_VERSION) return { ok: false, problem: 'an answer in a version this server does not speak' };
    if (typeof b['request'] !== 'string' || !UUID.test(b['request']) || b['request'] !== expected.request) return { ok: false, problem: 'an answer to another request' };
    if (typeof b['company'] !== 'string' || !GUID.test(b['company']) || b['company'].toLowerCase() !== expected.company.toLowerCase()) {
        return { ok: false, problem: 'an answer from another TallyPrime company' };
    }
    if (typeof b['ledger'] !== 'string' || b['ledger'].toLowerCase() !== expected.ledger.toLowerCase()) return { ok: false, problem: 'an answer for another ledger' };
    if (b['from'] !== expected.from || b['to'] !== expected.to) return { ok: false, problem: 'an answer for another period' };
    const binding: LedgerBinding = { request: expected.request, company: expected.company, ledger: expected.ledger.toLowerCase(), from: expected.from, to: expected.to };
    const outcome = b['outcome'];
    if (outcome !== 'read') {
        if (typeof outcome !== 'string' || !(LEDGER_FAILURES as readonly string[]).includes(outcome)) return { ok: false, problem: 'an outcome this server does not know' };
        return { ok: true, answer: { v: LEDGER_PROTOCOL_VERSION, ...binding, outcome: outcome as LedgerFailure } };
    }
    for (const field of ['openingPaise', 'closingPaise', 'tallyClosingPaise'] as const) {
        if (!isPaise(b[field])) return { ok: false, problem: `${field} is not a whole number of paise` };
    }
    if (typeof b['readAt'] !== 'string' || Number.isNaN(Date.parse(b['readAt']))) return { ok: false, problem: 'no time of reading' };
    const raw = b['lines'];
    if (!Array.isArray(raw)) return { ok: false, problem: 'no lines' };
    if (raw.length > LEDGER_MAX_LINES) return { ok: false, problem: `more than ${LEDGER_MAX_LINES} lines` };
    const lines: LedgerLine[] = [];
    let previous = expected.from;
    let movement = 0;
    for (const [i, item] of raw.entries()) {
        const l = (typeof item === 'object' && item !== null ? item : {}) as Record<string, unknown>;
        const where = `line ${i + 1}`;
        if (!isIsoDate(l['date']) || l['date'] < expected.from || l['date'] > expected.to) return { ok: false, problem: `${where} is dated outside the period` };
        if (l['date'] < previous) return { ok: false, problem: `${where} is out of date order` };
        previous = l['date'];
        if (!text(l['type'], 60)) return { ok: false, problem: `${where} has no voucher type` };
        if (!(l['number'] === null || l['number'] === undefined || text(l['number'], 60, true))) return { ok: false, problem: `${where} has an unreadable voucher number` };
        if (!isCount(l['debitPaise']) || !isCount(l['creditPaise'])) return { ok: false, problem: `${where} has an amount that is not whole paise` };
        if (l['debitPaise'] > 0 && l['creditPaise'] > 0) return { ok: false, problem: `${where} is both a debit and a credit` };
        const rawBills = l['bills'] ?? [];
        if (!Array.isArray(rawBills) || rawBills.length > LEDGER_MAX_BILLS_PER_LINE) return { ok: false, problem: `${where} names too many bills` };
        const bills: LedgerBill[] = [];
        for (const rb of rawBills) {
            const bill = (typeof rb === 'object' && rb !== null ? rb : {}) as Record<string, unknown>;
            if (!text(bill['ref'], 100, true) || !(LEDGER_BILL_KINDS as readonly unknown[]).includes(bill['kind']) || !isPaise(bill['amountPaise'])) {
                return { ok: false, problem: `${where} names a bill that cannot be read` };
            }
            bills.push({ ref: bill['ref'] as string, kind: bill['kind'] as LedgerBillKind, amountPaise: bill['amountPaise'] as number });
        }
        movement += (l['debitPaise'] as number) - (l['creditPaise'] as number);
        lines.push({
            date: l['date'], type: (l['type'] as string).trim(),
            number: typeof l['number'] === 'string' && l['number'].trim() !== '' ? l['number'].trim() : null,
            debitPaise: l['debitPaise'] as number, creditPaise: l['creditPaise'] as number, bills,
        });
    }
    // The check that matters: TallyPrime's opening and every line come to the closing, to the paisa.
    if ((b['openingPaise'] as number) + movement !== b['closingPaise']) {
        return { ok: false, problem: 'the opening balance and the lines do not come to the closing balance', didNotAddUp: true };
    }
    return {
        ok: true,
        answer: {
            v: LEDGER_PROTOCOL_VERSION, ...binding, outcome: 'read',
            openingPaise: b['openingPaise'] as number, closingPaise: b['closingPaise'] as number,
            tallyClosingPaise: b['tallyClosingPaise'] as number, lines, readAt: new Date(Date.parse(b['readAt'] as string)).toISOString(),
        },
    };
}

/* ---------------------------------------------------------------------------------------------------- */
/* The display                                                                                           */
/* ---------------------------------------------------------------------------------------------------- */

export interface LedgerRow extends LedgerLine { balancePaise: number }

export interface LedgerView {
    from: string;
    to: string;
    openingPaise: number;
    debitsPaise: number;
    creditsPaise: number;
    closingPaise: number;
    rows: LedgerRow[];
}

/** The ledger as it is read: each line with the balance after it, and the period's totals. */
export function ledgerView(read: Pick<LedgerRead, 'from' | 'to' | 'openingPaise' | 'closingPaise' | 'lines'>): LedgerView {
    let balance = read.openingPaise;
    let debits = 0, credits = 0;
    const rows = read.lines.map(line => {
        balance += line.debitPaise - line.creditPaise;
        debits += line.debitPaise;
        credits += line.creditPaise;
        return { ...line, balancePaise: balance };
    });
    return { from: read.from, to: read.to, openingPaise: read.openingPaise, debitsPaise: debits, creditsPaise: credits, closingPaise: read.closingPaise, rows };
}

/** A balance's side, as the books say it: Dr owed to the company, Cr held for the customer. */
export const ledgerSide = (paise: number): 'Dr' | 'Cr' | null => (paise > 0 ? 'Dr' : paise < 0 ? 'Cr' : null);

export const LEDGER_BILL_WORDS: Record<LedgerBillKind, string> = {
    new_ref: 'new bill', agst_ref: 'against', advance: 'advance', on_account: 'on account', other: '',
};

/** The bills a line touched, in a few words: "TA-001", "on account", "TA-001, TA-002". */
export function ledgerBillWords(bills: readonly LedgerBill[]): string {
    const words = bills.map(b => (b.kind === 'on_account' || !b.ref ? LEDGER_BILL_WORDS[b.kind] || '' : b.ref)).filter(Boolean);
    return [...new Set(words)].join(', ');
}

const rupees = (paise: number) => Math.round(paise) / 100;

/**
 * The ledger as a spreadsheet: the opening balance, a row per voucher, the closing balance. Debit and credit
 * in their own columns and the balance signed (positive owed, negative in credit), so every column adds up in
 * any spreadsheet. The currency is said once, in the headings. Nothing about how the customer paid.
 */
export function ledgerCsv(view: LedgerView, currency: string): string {
    interface Row { date: string; type: string; number: string; bills: string; debit: number | null; credit: number | null; balance: number }
    const columns: CsvColumn<Row>[] = [
        { header: 'Date', cell: r => dateCell(r.date) },
        { header: 'Voucher type', cell: r => textCell(r.type) },
        { header: 'Voucher number', cell: r => textCell(r.number) },
        { header: 'Bills', cell: r => textCell(r.bills) },
        { header: `Debit (${currency})`, cell: r => numberCell(r.debit) },
        { header: `Credit (${currency})`, cell: r => numberCell(r.credit) },
        { header: `Balance (${currency}; positive is owed)`, cell: r => numberCell(r.balance) },
    ];
    const rows: Row[] = [
        { date: view.from, type: 'Opening balance', number: '', bills: '', debit: null, credit: null, balance: rupees(view.openingPaise) },
        ...view.rows.map(r => ({
            date: r.date, type: r.type, number: r.number ?? '', bills: ledgerBillWords(r.bills),
            debit: r.debitPaise ? rupees(r.debitPaise) : null, credit: r.creditPaise ? rupees(r.creditPaise) : null, balance: rupees(r.balancePaise),
        })),
        { date: view.to, type: 'Closing balance', number: '', bills: '', debit: rupees(view.debitsPaise), credit: rupees(view.creditsPaise), balance: rupees(view.closingPaise) },
    ];
    return toCsv(columns, rows);
}

export const ledgerCsvFileName = (customer: string, view: Pick<LedgerView, 'from' | 'to'>) =>
    csvFileName(`ledger-${view.from}-to-${view.to}`, customer, view.to);

/* ---------------------------------------------------------------------------------------------------- */
/* Where a request stands, in words                                                                      */
/* ---------------------------------------------------------------------------------------------------- */

export type LedgerStatus = 'pending' | 'claimed' | 'completed' | 'failed' | 'expired' | 'cancelled';

/** Why a request ended without a ledger, as the server records it: the connector's reasons and the server's. */
export type LedgerEnd = LedgerFailure | 'invalid_answer' | 'connection_changed' | 'customer_changed' | 'not_picked_up' | 'no_answer';

/** What the person is told when there is no ledger to show (§10), never a blank page. */
export const LEDGER_END_WORDS: Record<LedgerEnd, string> = {
    tally_unavailable: 'TallyPrime isn’t open on the office computer, so the ledger can’t be read now.',
    company_not_loaded: 'TallyPrime on the office computer isn’t showing this company. Open it there, then try again.',
    company_changed: 'The office computer is set to another TallyPrime company now, so this ledger was not read.',
    not_a_debtor: 'This customer’s ledger is no longer in TallyPrime as a customer.',
    too_long: 'More than 5,000 entries. Choose a shorter period.',
    did_not_add_up: 'TallyPrime’s figures for this ledger did not add up, so nothing is shown. Try again after the next update.',
    timeout: 'TallyPrime took too long. Try a shorter period.',
    refused: 'TallyPrime would not give this ledger. Try again in a moment.',
    invalid_answer: 'The office computer sent a ledger that could not be checked, so nothing is shown. Try again.',
    connection_changed: 'The office computer’s connection changed while this was asked. Try again.',
    customer_changed: 'This customer was linked to another TallyPrime ledger while this was asked. Try again.',
    not_picked_up: 'The office computer did not answer. Try again.',
    no_answer: 'The office computer started reading the ledger but did not finish. Try again.',
};
